/**
 * Synthetic static-tag row for the bulk-edit source country (current selection).
 */

import type { PreparedDrop } from '@/features/smswiser-campaigns/types/workspace.types';
import { isScheduledSentPrepared } from '@/features/smswiser-campaigns/components/MultiCountrySentScheduledEdit/shared';

import type { StaticTagPropagationMatchKind } from './staticTagPropagation.types';

export interface BulkSourceCountryGroup {
  countryKey: string;
  tags: Array<{ tagName: string; draftCount: number }>;
  totalDrafts: number;
  isBulkSourceCountry: true;
}

function inSourceRow(d: PreparedDrop, kind: StaticTagPropagationMatchKind): boolean {
  if (kind === 'scheduled_sent') return isScheduledSentPrepared(d);
  return d.status === 'DRAFT';
}

export function buildBulkSourceCountryGroup(p: {
  allDrops: PreparedDrop[];
  selectedIds: readonly string[];
  sourceCountryKey: string;
  selectedTagNames: Set<string>;
  matchKind?: StaticTagPropagationMatchKind;
}): BulkSourceCountryGroup | null {
  const { allDrops, selectedIds, sourceCountryKey, selectedTagNames, matchKind = 'draft' } = p;
  if (!sourceCountryKey) return null;
  const sel = new Set(selectedIds);
  const inSource = allDrops.filter(
    (d) => sel.has(d.id) && d.countryKey === sourceCountryKey && inSourceRow(d, matchKind),
  );
  if (inSource.length === 0) return null;

  const tagToDraftIds = new Map<string, Set<string>>();
  for (const d of inSource) {
    const name = d.staticTag?.name;
    if (!name || !selectedTagNames.has(name)) continue;
    const set = tagToDraftIds.get(name) ?? new Set<string>();
    set.add(d.id);
    tagToDraftIds.set(name, set);
  }

  const tags = [...tagToDraftIds.entries()]
    .map(([tagName, ids]) => ({ tagName, draftCount: ids.size }))
    .sort((a, b) => a.tagName.localeCompare(b.tagName));

  const taggedDraftIds = new Set<string>();
  for (const ids of tagToDraftIds.values()) for (const id of ids) taggedDraftIds.add(id);

  return {
    countryKey: sourceCountryKey,
    tags,
    totalDrafts: taggedDraftIds.size > 0 ? taggedDraftIds.size : inSource.length,
    isBulkSourceCountry: true,
  };
}
